/*
  Bottom-left box on the vendor bill page: QuickBooks link, link status per order,
  where the bill came from (AP ingestion email / queued CSV bill) and the change log.
*/
import _ from 'lodash';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import accounting from 'accounting';
import { Link } from 'react-router-dom';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import OpenLink from '@material-ui/icons/OpenInNew';

import FMPRegionHeader from '../../components/ui/FMPRegionHeader';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';

const green = '#4CAF50';
const orange = '#FF9800';
const red = '#FF500F';
const blue = '#2196F3';

const styles = () => ({
	paper: {
		width: '100%',
		textAlign: 'left',
		boxSizing: 'border-box',
		padding: '20px 20px 30px 20px',
		marginTop: 5,
	},
	table: {
		width: '100%',
		borderCollapse: 'collapse',
		fontSize: 14,
		'& td': {
			padding: '6px 4px',
			borderBottom: '1px solid #eee',
			verticalAlign: 'top',
		},
		'& td:first-child': {
			color: '#777',
			width: 150,
			whiteSpace: 'nowrap',
		},
	},
	strong: {
		fontWeight: 900,
	},
	subtle: {
		color: '#888',
		fontSize: 12,
	},
	qbLink: {
		display: 'inline-flex',
		alignItems: 'center',
		fontWeight: 900,
		'& svg': {
			fontSize: 16,
			marginLeft: 4,
		},
	},
	logContainer: {
		maxHeight: 260,
		overflowY: 'auto',
		border: '1px solid #eee',
		marginTop: 10,
	},
	logRow: {
		padding: '6px 8px',
		borderBottom: '1px solid #f3f3f3',
		fontSize: 13,
	},
	empty: {
		color: '#999',
		fontStyle: 'italic',
		fontSize: 13,
		marginTop: 10,
	},
});

const statusDisplayMap = {
	pending: { text: 'Pending', color: blue },
	completed: { text: 'Completed', color: green },
	sent: { text: 'Sent (Missing QB)', color: red },
	invalid: { text: 'Marked Invalid', color: red },
};

const linkStatusDisplayMap = {
	notFullyLinked: { text: 'Not Fully Linked', color: orange },
	fullyLinked: { text: '100% Linked', color: green },
	allMarkedTransfers: { text: '100% Marked Transfers', color: green },
	allLinkedAndMarkedTransfers: { text: '100% Linked & Transfers', color: green },
};

const matchResultStatusColors = {
	allDataMatched: green,
	partialMatch: orange,
	noMatch: red,
};

const formatDateTime = (value) => {
	if (!value) {
		return 'n/a';
	}
	return moment(value).format('M/D/YYYY h:mm a');
};

const formatDate = (value) => {
	if (!value) {
		return 'n/a';
	}
	return moment(value).format('M/D/YYYY');
};

const userName = (user) => {
	if (!user) {
		return 'FMP';
	}
	return _.compact([user.firstName, user.lastName]).join(' ') || user.email || 'Unknown';
};

class VendorBillInfoBox extends Component {
	state = {
		details: undefined,
		isLoadingDetails: false,
	};

	componentDidMount() {
		this.loadDetails();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.vendorBillId !== this.props.vendorBillId) {
			this.loadDetails();
		}
	}

	loadDetails = () => {
		const { vendorBillId, getVendorBillDetails } = this.props;
		if (!vendorBillId) {
			return;
		}
		this.setState({ isLoadingDetails: true });
		getVendorBillDetails({
			params: { vendorBillId },
			callback: (err, details) => {
				//- details are extra context; the rest of the box still renders without them
				this.setState({ isLoadingDetails: false, details: err ? null : details });
			},
		});
	};

	renderColored = ({ text, color }) => {
		const { classes } = this.props;
		return (
			<span className={classes.strong} style={{ color }}>
				{text}
			</span>
		);
	};

	renderOrderLink = (vendorBillOrder) => {
		const {
			bolNumberToInvoiceMap = {},
			carrierPoNumberToInvoiceMap = {},
			railcarMap = {},
		} = this.props;
		const {
			bolNumberToInvoiceIds,
			carrierPoNumberToInvoiceIds,
			linkedRailcarId,
			linkedBulkDeliveryId,
			isRackToRackTransfer,
			isInventoryTransfer,
		} = vendorBillOrder;

		const links = [];
		const pushInvoiceLinks = (linkRows) => {
			linkRows.forEach((linkRow) => {
				if (!linkRow) {
					return;
				}
				const { stationInvoiceId, bulkInvoiceId, rackTransferId } = linkRow;
				if (stationInvoiceId) {
					links.push(
						<Link
							key={`s${stationInvoiceId}`}
							to={`/accounting/stationInvoice/update/${stationInvoiceId}`}
						>
							Station invoice #{stationInvoiceId}
						</Link>
					);
				}
				if (bulkInvoiceId) {
					links.push(
						<Link key={`b${bulkInvoiceId}`} to={`/accounting/bulkInvoice/update/${bulkInvoiceId}`}>
							Bulk invoice #{bulkInvoiceId}
						</Link>
					);
				}
				if (rackTransferId) {
					links.push(
						<Link
							key={`r${rackTransferId}`}
							to={`/accounting/rackTransfer/update/${rackTransferId}`}
						>
							Rack transfer #{rackTransferId}
						</Link>
					);
				}
			});
		};

		pushInvoiceLinks(_.map(bolNumberToInvoiceIds, (id) => bolNumberToInvoiceMap[id]));
		pushInvoiceLinks(_.map(carrierPoNumberToInvoiceIds, (id) => carrierPoNumberToInvoiceMap[id]));

		const linkCount = _.size(bolNumberToInvoiceIds) + _.size(carrierPoNumberToInvoiceIds);
		if (linkCount > 0 && _.isEmpty(links)) {
			links.push(<span key="count">{linkCount} invoice link(s)</span>);
		}

		if (linkedRailcarId) {
			const railcar = railcarMap[linkedRailcarId];
			links.push(
				<span key="railcar">Railcar #{railcar ? railcar.railcarNumber : linkedRailcarId}</span>
			);
		}

		const transfers = [];
		if (isRackToRackTransfer) {
			transfers.push('Rack to rack transfer');
		}
		if (isInventoryTransfer) {
			transfers.push('Inventory transfer');
		}
		if (linkedBulkDeliveryId) {
			transfers.push(`Bulk delivery #${linkedBulkDeliveryId}`);
		}

		if (_.isEmpty(links) && _.isEmpty(transfers)) {
			return this.renderColored({ text: 'Not linked', color: orange });
		}

		return (
			<Fragment>
				{!_.isEmpty(links) && this.renderColored({ text: 'Linked', color: green })}
				{_.isEmpty(links) && this.renderColored({ text: 'Marked transfer', color: green })}
				{links.map((link, i) => (
					<div key={i}>{link}</div>
				))}
				{transfers.map((transfer) => (
					<div key={transfer}>{transfer}</div>
				))}
			</Fragment>
		);
	};

	renderSource = () => {
		const { classes, vendorBill, splitBillPageId } = this.props;
		const { details, isLoadingDetails } = this.state;

		const rows = [];

		if (isLoadingDetails) {
			rows.push(
				<tr key="loading">
					<td>Source</td>
					<td className={classes.subtle}>Loading...</td>
				</tr>
			);
		} else if (details) {
			const { apIngestionResults, queuedVendorBills } = details;

			apIngestionResults.forEach((result) => {
				const {
					apIngestionResultId,
					apIngestionId,
					matchResultStatus,
					matchResultStatusMessage,
					emailFrom,
					emailSubject,
					emailDateSent,
					dateCreated,
				} = result;
				rows.push(
					<tr key={`ap${apIngestionResultId}`}>
						<td>AP Ingestion</td>
						<td>
							<Link to={`/apAutomation/apIngestionResult/triage/${apIngestionResultId}`}>
								Result #{apIngestionResultId}
							</Link>{' '}
							<span className={classes.subtle}>
								(ingestion #{apIngestionId}, {formatDateTime(dateCreated)})
							</span>
							{matchResultStatus && (
								<div>
									Match:{' '}
									{this.renderColored({
										text: matchResultStatus,
										color: matchResultStatusColors[matchResultStatus] || '#616161',
									})}
								</div>
							)}
							{matchResultStatusMessage && (
								<div className={classes.subtle}>{matchResultStatusMessage}</div>
							)}
							{emailSubject && (
								<div style={{ marginTop: 4 }}>
									<div>
										Email: <span className={classes.strong}>{emailSubject}</span>
									</div>
									<div className={classes.subtle}>
										From {emailFrom} on {formatDateTime(emailDateSent)}
									</div>
								</div>
							)}
						</td>
					</tr>
				);
			});

			queuedVendorBills.forEach((queuedVendorBill) => {
				const {
					queuedVendorBillId,
					queueStatus,
					pendingReason,
					dateCreated,
					dateVendorBillCreated,
				} = queuedVendorBill;
				rows.push(
					<tr key={`q${queuedVendorBillId}`}>
						<td>Queued Bill (CSV)</td>
						<td>
							<Link to={`/billing/queuedVendorBill/view/${queuedVendorBillId}`}>
								Queued bill #{queuedVendorBillId}
							</Link>{' '}
							<span className={classes.subtle}>({queueStatus})</span>
							<div className={classes.subtle}>
								Queued {formatDateTime(dateCreated)}, bill created{' '}
								{formatDateTime(dateVendorBillCreated)}
							</div>
							{pendingReason && <div className={classes.subtle}>{pendingReason}</div>}
						</td>
					</tr>
				);
			});

			if (_.isEmpty(apIngestionResults) && _.isEmpty(queuedVendorBills)) {
				rows.push(
					<tr key="manual">
						<td>Source</td>
						<td>Entered manually</td>
					</tr>
				);
			}
		}

		let documentDisplay = 'No document attached';
		if (splitBillPageId || vendorBill.splitBillPageId) {
			documentDisplay = `Split bill page #${splitBillPageId || vendorBill.splitBillPageId}`;
		} else if (vendorBill.vendorDocumentResourceId) {
			documentDisplay = 'Uploaded PDF';
		} else if (vendorBill.vendorCsvResourceId) {
			documentDisplay = 'LF / PDI CSV file';
		}
		rows.push(
			<tr key="document">
				<td>Document</td>
				<td>{documentDisplay}</td>
			</tr>
		);

		return rows;
	};

	render() {
		const {
			classes,
			vendorBill,
			quickbooksVendorMap = {},
			quickbooksTermMap = {},
			userMap = {},
		} = this.props;
		const { details, isLoadingDetails } = this.state;

		if (!vendorBill) {
			return null;
		}

		const {
			vendorBillId,
			quickbooksBillId,
			quickbooksBillUrl,
			quickbooksVendorId,
			quickbooksTermId,
			dueDate,
			total,
			status,
			linkStatus,
			linkMessage,
			invalidReason,
			createdByUserId,
			dateCreated,
			vendorBillOrders = [],
		} = vendorBill;

		const isVendorCredit = total < 0;
		const quickbooksVendor = quickbooksVendorMap[quickbooksVendorId];
		const quickbooksTerm = quickbooksTermMap[quickbooksTermId];

		const createdBy = (details && details.createdBy) || userMap[createdByUserId];
		const logs = (details && details.logs) || [];

		return (
			<Fragment>
				<FMPRegionHeader label={'QuickBooks & Link Status'} isSmaller={true} />
				<Paper className={classes.paper}>
					<table className={classes.table}>
						<tbody>
							<tr>
								<td>QuickBooks</td>
								<td>
									{quickbooksBillUrl && (
										<a
											href={quickbooksBillUrl}
											target="_blank"
											rel="noopener noreferrer"
											className={classes.qbLink}
										>
											Open {isVendorCredit ? 'Vendor Credit' : 'Bill'} #{quickbooksBillId} in
											QuickBooks
											<OpenLink />
										</a>
									)}
									{!quickbooksBillUrl &&
										this.renderColored({ text: 'Not exported to QuickBooks yet', color: orange })}
								</td>
							</tr>
							<tr>
								<td>QB Vendor</td>
								<td>
									{quickbooksVendor
										? quickbooksVendor.DisplayName || quickbooksVendor.name
										: 'Not found'}{' '}
									<span className={classes.subtle}>(id {quickbooksVendorId || 'n/a'})</span>
								</td>
							</tr>
							<tr>
								<td>QB Terms / Due</td>
								<td>
									{quickbooksTerm
										? quickbooksTerm.Name
										: `Not found (id ${quickbooksTermId || 'n/a'})`}
									, due {formatDate(dueDate)}
								</td>
							</tr>
							<tr>
								<td>Saved Total</td>
								<td>
									{_.isNil(total) ? 'n/a' : accounting.formatMoney(total)}
									{isVendorCredit && <span className={classes.subtle}> (vendor credit)</span>}
								</td>
							</tr>
							<tr>
								<td>Bill Status</td>
								<td>
									{this.renderColored(
										statusDisplayMap[status] || { text: status || 'n/a', color: '#616161' }
									)}
									{invalidReason && <div className={classes.subtle}>Reason: {invalidReason}</div>}
								</td>
							</tr>
							<tr>
								<td>Link Status</td>
								<td>
									{this.renderColored(
										linkStatusDisplayMap[linkStatus] || {
											text: linkStatus || 'n/a',
											color: '#616161',
										}
									)}
									{linkMessage && <span className={classes.subtle}> ({linkMessage})</span>}
								</td>
							</tr>
							{vendorBillOrders.map((vendorBillOrder, orderIndex) => {
								const { vendorBillOrderId, bolNumber, carrierPoNumber } = vendorBillOrder;
								let reference = '';
								if (bolNumber) {
									reference = `BOL ${bolNumber}`;
								} else if (carrierPoNumber) {
									reference = `PO ${carrierPoNumber}`;
								}
								return (
									<tr key={vendorBillOrderId || orderIndex}>
										<td>
											Order #{orderIndex + 1}
											{reference && <div className={classes.subtle}>{reference}</div>}
										</td>
										<td>{this.renderOrderLink(vendorBillOrder)}</td>
									</tr>
								);
							})}
							{this.renderSource()}
							<tr>
								<td>Created</td>
								<td>
									{formatDateTime(dateCreated)} by {userName(createdBy)}
									<span className={classes.subtle}> (FMP bill #{vendorBillId})</span>
								</td>
							</tr>
						</tbody>
					</table>

					<FMPSectionHeader label={'Activity Log'} />
					{!isLoadingDetails && _.isEmpty(logs) && (
						<div className={classes.empty}>No activity logged for this bill</div>
					)}
					{!_.isEmpty(logs) && (
						<div className={classes.logContainer}>
							{logs.map((log) => (
								<div key={log.vendorBillLogId} className={classes.logRow}>
									<div>{log.entry}</div>
									<div className={classes.subtle}>
										{formatDateTime(log.dateCreated)} by {userName(log.firstName ? log : null)}
									</div>
								</div>
							))}
						</div>
					)}
				</Paper>
			</Fragment>
		);
	}
}

VendorBillInfoBox.propTypes = {
	vendorBill: PropTypes.object,
	vendorBillId: PropTypes.number,
	splitBillPageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	getVendorBillDetails: PropTypes.func.isRequired,

	quickbooksVendorMap: PropTypes.object,
	quickbooksTermMap: PropTypes.object,
	userMap: PropTypes.object,
	bolNumberToInvoiceMap: PropTypes.object,
	carrierPoNumberToInvoiceMap: PropTypes.object,
	railcarMap: PropTypes.object,

	classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(VendorBillInfoBox);
